import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  MoreHorizontal,
  Route as RouteIcon,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { formatApiError } from "@/lib/format-api-error";
import { formatRelative } from "@/lib/utils";
import { useArchiveJourney } from "./use-archive-journey";
import { useUpdateJourney } from "./use-update-journey";
import { useJourneys } from "./use-journeys";
import { CreateJourneyDialog } from "./create-journey-dialog";
import type { JourneyListRow } from "./types";

// Phase 7 sub-bucket 14 — admin journeys list. Ports /app/admin/journeys/page.tsx
// stripped of the Saved Views toolbar (deferred to admin deals) — search +
// archived toggle covers the active filtering need. Counts (task_count +
// assignment_count) come pre-aggregated from listJourneysWithCounts.
//
// Phase 6 mutation hooks (useArchiveJourney, useUpdateJourney for restore)
// already invalidate queryKeys.journeys.list(); both active and archived
// caches refresh on toggle.

export function JourneysListPage() {
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const query = useJourneys({ includeArchived: showArchived });
  const rows = query.data ?? [];

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(term) ||
        (r.description ?? "").toLowerCase().includes(term),
    );
  }, [rows, search]);

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Journeys
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Define partner onboarding journeys — a sequence of tasks
            (forms, links, files, notes) the partner completes after
            assignment. Assign journeys to partners on the editor's
            Assignments tab.
          </p>
        </div>
        <CreateJourneyDialog />
      </header>

      <Toolbar
        search={search}
        onSearchChange={setSearch}
        showArchived={showArchived}
        onToggleArchived={() => setShowArchived((v) => !v)}
      />

      <div className="mt-4">
        {query.isLoading ? (
          <TableSkeleton />
        ) : query.isError ? (
          <EmptyState
            icon={ArrowLeft}
            title="Couldn't load journeys"
            description={query.error?.message ?? "Try refreshing."}
          />
        ) : filtered.length > 0 ? (
          <JourneysTable rows={filtered} showArchived={showArchived} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={RouteIcon}
            title={showArchived ? "No archived journeys" : "No journeys yet"}
            description={
              showArchived
                ? "Switch back to Active to see live journeys."
                : "Create a journey to start running partner onboarding."
            }
            action={!showArchived ? <CreateJourneyDialog variant="empty" /> : undefined}
          />
        ) : (
          <EmptyState
            icon={Search}
            title="No matches"
            description={`No journey matches "${search.trim()}". Clear the search to see all rows.`}
          />
        )}
      </div>
    </div>
  );
}

function Toolbar({
  search,
  onSearchChange,
  showArchived,
  onToggleArchived,
}: {
  search: string;
  onSearchChange: (s: string) => void;
  showArchived: boolean;
  onToggleArchived: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[220px] max-w-md">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search journeys"
          className="pl-8"
        />
      </div>
      <div className="ml-auto flex items-center gap-1 rounded-md border bg-card p-0.5">
        <button
          type="button"
          onClick={showArchived ? onToggleArchived : undefined}
          className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
            !showArchived
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Active
        </button>
        <button
          type="button"
          onClick={!showArchived ? onToggleArchived : undefined}
          className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
            showArchived
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Archived
        </button>
      </div>
    </div>
  );
}

function JourneysTable({
  rows,
  showArchived,
}: {
  rows: JourneyListRow[];
  showArchived: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-2.5">Journey</th>
            <th className="px-4 py-2.5 text-right">Tasks</th>
            <th className="px-4 py-2.5 text-right">Assignments</th>
            <th className="px-4 py-2.5 text-right">Last updated</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {rows.map((r) => (
            <JourneyRow key={r.id} row={r} showArchived={showArchived} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JourneyRow({
  row,
  showArchived,
}: {
  row: JourneyListRow;
  showArchived: boolean;
}) {
  const archive = useArchiveJourney();
  const updateJourney = useUpdateJourney();
  const busy = archive.isPending || updateJourney.isPending;

  function handleArchive() {
    archive.mutate(row.id, {
      onSuccess: () => toast.success("Journey archived"),
      onError: (err) => toast.error(formatApiError(err, "Failed to archive")),
    });
  }

  function handleRestore() {
    updateJourney.mutate(
      { id: row.id, patch: { is_archived: false } },
      {
        onSuccess: () => toast.success("Journey restored"),
        onError: (err) => toast.error(formatApiError(err, "Failed to restore")),
      },
    );
  }

  return (
    <tr className="group transition-colors hover:bg-muted/30">
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/admin/journeys/${row.id}`}
            className="font-medium text-foreground hover:text-accent"
          >
            {row.name}
          </Link>
          {showArchived && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border/70 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              <Archive className="h-3 w-3" /> Archived
            </span>
          )}
        </div>
        {row.description && (
          <p className="mt-0.5 line-clamp-2 max-w-2xl text-xs text-muted-foreground">
            {row.description}
          </p>
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-foreground/90">
        {row.task_count}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-foreground/90">
        {row.assignment_count}
      </td>
      <td className="px-4 py-3 text-right text-xs text-muted-foreground">
        {formatRelative(row.updated_at)}
      </td>
      <td className="px-4 py-3 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              aria-label={`Actions for ${row.name}`}
              disabled={busy}
              className="opacity-0 transition group-hover:opacity-100 data-[state=open]:opacity-100"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/admin/journeys/${row.id}`}>Open editor</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {row.is_archived ? (
              <DropdownMenuItem onClick={handleRestore} disabled={busy}>
                <ArchiveRestore className="mr-2 h-3.5 w-3.5" /> Restore
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={handleArchive} disabled={busy}>
                <Archive className="mr-2 h-3.5 w-3.5" /> Archive
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

function TableSkeleton() {
  return (
    <div className="rounded-xl border bg-card">
      <div className="space-y-3 p-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="ml-auto h-4 w-12" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
